import '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';

/**
 * Utility ah-pwdless-common-utility
 * @description utility to support webbio and u2f authentication
 * @copyright 2020-2-20
 * @file
 * @class 
 * @export
 */

// Utility implementation here.
// Utilities can export anything: singletons, classes, even pure functions.

class AhPwdlessCommonsUtility {
  constructor(options) {
    this.$ = "";
    this._ = "";
    this.GF = spartaGlobalFunctions;
  }

  /**
   * @function _publicKeyCredentialToJSON
   * @description Converts public key credentials to a json.
   * @param {Array|ArrayBuffer|Object} pubKeyCred - The public key credential to convert.
   * @returns {Array|ArrayBuffer|Object} The credential converted to a JSON- compatible format.
  */

  _publicKeyCredentialToJSON(pubKeyCred) {
    const that = this;
    /* ----- DO NOT MODIFY THIS CODE ----- */
    if (pubKeyCred instanceof Array) {
      let arr = [];
      for (let i of pubKeyCred) arr.push(this._publicKeyCredentialToJSON(i));
      return arr;
    }
    if (typeof pubKeyCred === 'function') {
      return;
    }
    if (pubKeyCred instanceof ArrayBuffer) {
      return that._encode(pubKeyCred);
    }
    if (pubKeyCred instanceof Object) {
      let obj = {};
      for (let key in pubKeyCred) {
        obj[key] = this._publicKeyCredentialToJSON(pubKeyCred[key]);
      }
      return obj;
    }
    return pubKeyCred;
  }

  /**
   * @function _encode
   * @description Encodes an arraybuffer string.
   * @param {String} ArrayBuffer - The arraybuffer to encode String.
   * @returns {Base64URL} The encoded binary data for an ArrayBuffer.
  */

  _encode(arraybuffer) {
    let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    // Use a lookup table to find the index.
    let lookup = new Uint8Array(256);
    for (let i = 0; i < chars.length; i++) {
      lookup[chars.charCodeAt(i)] = i;
    }
    let bytes = new Uint8Array(arraybuffer),
      i,
      len = bytes.length,
      base64url = '';
    for (i = 0; i < len; i += 3) {
      base64url += chars[bytes[i] >> 2];
      base64url += chars[(bytes[i] & 3) << 4 | bytes[i + 1] >> 4];
      base64url += chars[(bytes[i + 1] & 15) << 2 | bytes[i + 2] >> 6];
      base64url += chars[bytes[i + 2] & 63];
    }
    if (len % 3 === 2) {
      base64url = base64url.substring(0, base64url.length - 1);
    } else if (len % 3 === 1) {
      base64url = base64url.substring(0, base64url.length - 2);
    }
    return base64url;
  }
  /**
   * @function _preformatGetAssertReq
   * @description Preformats and decodes the get assertion request for WebAuth. 
   * @param {Object} getAssert - The assertion request object to be preformatted.
   * @returns {Object} The prefomatted assertion request object with decoded fields.
  */

  _preformatGetAssertReq(getAssert) {
    const self = this;
    /* ----- DO NOT MODIFY THIS CODE ----- */
    getAssert.challenge = self._decode(getAssert.challenge);
    for (let allowCred of getAssert.allowCredentials) {
      allowCred.id = self._decode(allowCred.id);
    }
    return getAssert;
  }
  y;

  /**
    * @function _decode
    * @description Decodes a Base64 encoded string to an ArrayBuffer.
    * @param {String} base64String - The Base64 encoded String to decode.
    * @returns {ArrayBuffer} The decoded binary data as an ArrayBuffer.
  */

  _decode(base64string) {
    let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    // Use a lookup table to find the index.
    let lookup = new Uint8Array(256);
    for (let i = 0; i < chars.length; i++) {
      lookup[chars.charCodeAt(i)] = i;
    }
    let bufferLength = base64string.length * 0.75,
      len = base64string.length,
      i,
      p = 0,
      encoded1,
      encoded2,
      encoded3,
      encoded4;
    let bytes = new Uint8Array(bufferLength);
    for (i = 0; i < len; i += 4) {
      encoded1 = lookup[base64string.charCodeAt(i)];
      encoded2 = lookup[base64string.charCodeAt(i + 1)];
      encoded3 = lookup[base64string.charCodeAt(i + 2)];
      encoded4 = lookup[base64string.charCodeAt(i + 3)];
      bytes[p++] = encoded1 << 2 | encoded2 >> 4;
      bytes[p++] = (encoded2 & 15) << 4 | encoded3 >> 2;
      bytes[p++] = (encoded3 & 3) << 6 | encoded4 & 63;
    }
    return bytes.buffer;
  }
  /**
  * @function _checkpwdlessEligibility
  * @description Determines if the selected device is eligible for passwordless login. 
  * @param {String} OID_SELECT_ID - The ID of the HTML select element containing device options.
  * @returns {boolean} Ture if the selected device is eligible for passwordless login, false otherwise.
  */

  _checkpwdlessEligibility(OID_SELECT_ID) {
    const LGNOPTSCookie = this.GF.spartaGetCookie('LGNOPTS').trim();
    let oidLGNOPTS = LGNOPTSCookie;
    if (LGNOPTSCookie && LGNOPTSCookie !== '""') {
      let oidLGNOPTSStr = atob(LGNOPTSCookie);
      let oidLGNOPTSObj = JSON.parse(oidLGNOPTSStr);
      if (oidLGNOPTSObj && oidLGNOPTSObj['web-bio']) {
        oidLGNOPTS = oidLGNOPTSObj['web-bio'];
      }
    }
    const dropDownOptionElem = document.getElementById(OID_SELECT_ID);
    const olbSigninPrefillMultiSecureCookie = this.GF.spartaGetCookie('olb_signin_prefill_multi_secure').trim();
    const onlineIdList = olbSigninPrefillMultiSecureCookie.split('||');
    let selectedEncryptedID;
    let selectedID = "";
    if (dropDownOptionElem && dropDownOptionElem.selectedIndex > -1) {
      selectedID = dropDownOptionElem.options[dropDownOptionElem.selectedIndex].text;
      for (let i = 0; i < onlineIdList.length; i++) {
        onlineIdList[i].split(':');
        if (onlineIdList[i].indexOf(selectedID) > -1) {
          selectedEncryptedID = onlineIdList[i].split(':')[1];
        }
      }
    }
    if (selectedID && oidLGNOPTS && oidLGNOPTS.indexOf(selectedEncryptedID) > -1) {
      return true;
    } else {
      return false;
    }
  }
}

export { AhPwdlessCommonsUtility as default };
